import React, { useEffect } from "react";
import { SVGS, TableRowSkeleton } from ".";
import {
  AddGuestSVG,
  InfoSVG,
  PersonalMessageSVG,
  RemoveGuestSVG,
} from "./SVG";

const Table = ({
  headers,
  isLoading,
  selectedData,
  totalGuests,
  renderPageNumbers,
  expandedRows,
  currentPage,
  setCurrentPage,
  totalPages,
  handleExpandRow,
  itemsPerPage,
  handleEditTable,
  isEditingTable,
  setIsEditingTable,
  loadingAddTable,
  selectedTable,
  setSelectedTable,
  numOfTables,
  isComingLoading,
  handleAttendanceChange,
}) => {
  return (
    <div className="shadow overflow-hidden border-b border-gray-200 rounded-2xl overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <TableHeader headers={headers} />
        <TableBody
          isLoading={isLoading}
          selectedData={selectedData}
          headers={headers}
          itemsPerPage={itemsPerPage}
          expandedRows={expandedRows}
          handleExpandRow={handleExpandRow}
          isEditingTable={isEditingTable}
          setIsEditingTable={setIsEditingTable}
          selectedTable={selectedTable}
          setSelectedTable={setSelectedTable}
          loadingAddTable={loadingAddTable}
          handleEditTable={handleEditTable}
          numOfTables={numOfTables}
          handleAttendanceChange={handleAttendanceChange}
          isComingLoading={isComingLoading}
        />
      </table>
      <Pagination
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        totalPages={totalPages}
        renderPageNumbers={renderPageNumbers}
        totalGuests={totalGuests}
      />
    </div>
  );
};

export default Table;

const TableHeader = ({ headers }) => {
  return (
    <thead className="bg-gray-50">
      <tr>
        {headers.map((header, index) => (
          <th
            key={index}
            scope="col"
            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
          >
            {header.name}
          </th>
        ))}
        <th
          scope="col"
          className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
        >
          Uredi
        </th>
      </tr>
    </thead>
  );
};

const TableBody = ({
  isLoading,
  selectedData,
  headers,
  itemsPerPage,
  expandedRows,
  handleExpandRow,
  isEditingTable,
  setIsEditingTable,
  selectedTable,
  setSelectedTable,
  loadingAddTable,
  handleEditTable,
  numOfTables,
  handleAttendanceChange,
  isComingLoading,
}) => {
  if (isLoading) {
    return (
      <tbody className="bg-white divide-y divide-gray-200">
        {Array.from({ length: itemsPerPage }).map((_, rowIndex) => (
          <tr key={rowIndex}>
            {Array.from({ length: headers.length }).map((_, headerIndex) => (
              <td
                key={headerIndex}
                className="px-6 py-4 whitespace-nowrap text-sm text-gray-500"
              >
                <TableRowSkeleton headers={headers} />
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    );
  }

  if (!selectedData?.length) {
    return (
      <tbody className="bg-white divide-y divide-gray-200">
        <tr className="w-full justify-center p-4 lg:mt-0 overflow-hidden opacity-25 max-h-10">
          <td
            colSpan={headers.length + 1}
            className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center max-h-10"
          >
            <div className="max-w-full sm:max-w-xl md:max-w-2xl lg:max-w-6xl xl:max-w-[85rem] mx-auto">
              <div className="flex flex-col justify-center gap-x-5 lg:gap-x-8 xl:gap-x-12 items-center">
                <div className="mt-4 lg:mt-0 overflow-hidden">
                  <SVGS.NoGuestsSVG />
                </div>
                <div className="col-span-1 lg:col-span-4">
                  <h1 className="font-bold text-gray-800 md:text-md lg:text-xl  ">
                    {selectedTable === 0
                      ? "Nema gostiju"
                      : `Nema gostiju na stolu ${selectedTable}`}
                  </h1>
                </div>
              </div>
            </div>
          </td>
        </tr>
      </tbody>
    );
  }

  return (
    <tbody className="bg-white divide-y divide-gray-200">
      {selectedData.map((item) => (
        <React.Fragment key={item.id}>
          <tr className={!item.isComing && "bg-gray-200"}>
            <NameRow
              item={item}
              handleExpandRow={handleExpandRow}
              expandedRows={expandedRows}
            />
            <GuestTableRow
              isEditingTable={isEditingTable}
              setIsEditingTable={setIsEditingTable}
              item={item}
              selectedTable={selectedTable}
              setSelectedTable={setSelectedTable}
              loadingAddTable={loadingAddTable}
              handleEditTable={handleEditTable}
              numOfTables={numOfTables}
              isAdditional={item.isAdditional && item.id}
            />
            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
              {item.needsAccommodation ? "DA" : "NE"}
            </td>
            <td
              className={`px-6 py-4 whitespace-nowrap text-sm ${
                item.isComing ? "text-amber-700" : "text-teal-500"
              }`}
            >
              <div className="hs-tooltip [--placement:bottom] inline-block">
                <button
                  onClick={() =>
                    handleAttendanceChange(item.id, true, !item.isComing)
                  }
                  className="hs-tooltip-toggle hover:scale-110 transition-transform duration-200 ease-in-out"
                  disabled={isComingLoading === item.id}
                >
                  {isComingLoading === item.id ? (
                    <span className="animate-spin inline-block w-6 h-6 border-4 border-current border-t-transparent text-teal-600 rounded-full" />
                  ) : !item?.isComing ? (
                    <AddGuestSVG />
                  ) : (
                    <RemoveGuestSVG />
                  )}
                  <span
                    className="hs-tooltip-content hs-tooltip-shown:opacity-100 hs-tooltip-shown:visible opacity-0 transition-opacity inline-block absolute invisible z-10 py-1 px-2 bg-gray-900 text-xs font-medium text-white rounded-lg shadow-sm  "
                    role="tooltip"
                  >
                    {item?.isComing ? "Ukloni gosta" : "Dodaj gosta"}
                  </span>
                </button>
              </div>
            </td>
          </tr>
          {expandedRows.includes(item.id) &&
            item.additionalGuests?.length > 0 &&
            item.additionalGuests.map((guest) => (
              <tr
                key={guest.id}
                className={guest.isComing ? "bg-gray-50" : "bg-gray-200"}
              >
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  + {guest.guestName}
                </td>
                <GuestTableRow
                  isEditingTable={isEditingTable}
                  setIsEditingTable={setIsEditingTable}
                  item={guest}
                  selectedTable={selectedTable}
                  setSelectedTable={setSelectedTable}
                  loadingAddTable={loadingAddTable}
                  handleEditTable={handleEditTable}
                  isAdditional={item.id}
                  numOfTables={numOfTables}
                  resId={item.id}
                />
                <td className="px-6 py-4 whitespace-nowrap text-sm text-amber-500 text-end">
                  {/* Additional guest details can be added here */}
                </td>
                <td
                  className={`px-6 py-4 whitespace-nowrap text-sm ${
                    guest.isComing ? "text-amber-700" : "text-teal-500"
                  }`}
                >
                  <div className="hs-tooltip [--placement:bottom] inline-block">
                    <button
                      onClick={() =>
                        handleAttendanceChange(guest.id, false, !guest.isComing)
                      }
                      className="hs-tooltip-toggle hover:scale-110 transition-transform duration-200 ease-in-out"
                      disabled={isComingLoading === guest.id}
                    >
                      {isComingLoading === guest.id ? (
                        <span className="animate-spin inline-block w-6 h-6 border-4 border-current border-t-transparent text-teal-600 rounded-full" />
                      ) : !guest?.isComing ? (
                        <AddGuestSVG />
                      ) : (
                        <RemoveGuestSVG />
                      )}
                      <span
                        className="hs-tooltip-content hs-tooltip-shown:opacity-100 hs-tooltip-shown:visible opacity-0 transition-opacity inline-block absolute invisible z-10 py-1 px-2 bg-gray-900 text-xs font-medium text-white rounded-lg shadow-sm  "
                        role="tooltip"
                      >
                        {guest?.isComing ? "Ukloni gosta" : "Dodaj gosta"}
                      </span>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
        </React.Fragment>
      ))}
    </tbody>
  );
};

const Pagination = ({
  currentPage,
  setCurrentPage,
  totalPages,
  renderPageNumbers,
  totalGuests,
}) => {
  return (
    <div className="flex flex-row bg-gray-50 items-center justify-between">
      <nav className="flex items-center space-x-2">
        <div className="flex items-center">
          <a
            className="text-gray-400 hover:text-teal-500 p-4 inline-flex items-center gap-2 font-medium rounded-md cursor-pointer"
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
          >
            <span aria-hidden="true">«</span>
            <span className="sr-only">Previous</span>
          </a>
          {renderPageNumbers()}
          <a
            className="text-gray-400 hover:text-teal-500 p-4 inline-flex items-center gap-2 font-medium rounded-md cursor-pointer"
            onClick={() => setCurrentPage(totalPages)}
            disabled={currentPage === totalPages}
          >
            <span className="sr-only">Next</span>
            <span aria-hidden="true">»</span>
          </a>
        </div>
      </nav>
      <h1 className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
        Ukupan broj gostiju: {totalGuests ? totalGuests : "0"}
      </h1>
    </div>
  );
};

const NameRow = ({ item, handleExpandRow, expandedRows }) => {
  useEffect(() => {
    if (window.HSTooltip) {
      window.HSTooltip.autoInit();
    }

    return () => {
      if (window.HSTooltip) {
        window.HSTooltip.destroy?.();
      }
    };
  }, [item]);
  return (
    <>
      <td
        className={`px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 w-full flex flex-row items-center justify-between ${
          item.additionalGuests?.length && "cursor-pointer"
        }`}
      >
        <div
          onClick={() => handleExpandRow(item.id)}
          className="flex align-middle"
        >
          <p className="inline-flex items-center">
            {item.guestName}
            {item.additionalGuests?.length ? (
              <span className="ml-2">
                {expandedRows.includes(item.id) ? (
                  <SVGS.AccordionOpenSVG />
                ) : (
                  <SVGS.AccordionClosedSVG />
                )}
              </span>
            ) : (
              ""
            )}
          </p>
        </div>
        <div className="flex flex-row mx-2">
          <div className="col-start-2 text-center">
            <div
              className={`hs-tooltip [--placement:bottom] inline-block ${
                !item?.personalMessage.length && "hidden"
              }`}
            >
              <button
                type="button"
                className="hs-tooltip-toggle size-10 inline-flex justify-center items-center gap-2 rounded-full bg-white/10 border border-white/30 text-teal-500 shadow-md shadow-gray-800/10 hover:bg-white hover:border-white hover:shadow-gray-800/10 transition"
              >
                <PersonalMessageSVG />
                <span
                  className="hs-tooltip-content hs-tooltip-shown:opacity-100 hs-tooltip-shown:visible opacity-0 transition-opacity inline-block absolute invisible z-10 py-1 px-2 bg-gray-900 text-xs font-medium text-white rounded-lg shadow-sm  "
                  role="tooltip"
                >
                  <div className="max-w-[20rem] whitespace-pre-wrap break-words text-justify">
                    {item?.personalMessage}
                  </div>
                </span>
              </button>
            </div>
          </div>
          <div className="col-start-2 text-center ml-4">
            <div
              className={`hs-tooltip [--placement:bottom] inline-block ${
                !item?.email?.length && "hidden"
              }`}
            >
              <button
                type="button"
                className="hs-tooltip-toggle size-10 inline-flex justify-center items-center gap-2 rounded-full bg-white/10 border border-white/30 text-teal-500 shadow-md shadow-gray-800/10 hover:bg-white hover:border-white hover:shadow-gray-800/10 transition"
              >
                <InfoSVG />
                <span
                  className="hs-tooltip-content hs-tooltip-shown:opacity-100 hs-tooltip-shown:visible opacity-0 transition-opacity inline-block absolute invisible z-10 py-1 px-2 bg-gray-900 text-xs font-medium text-white rounded shadow-sm  "
                  role="tooltip"
                >
                  <div>{item?.email}</div>
                  <div>{item?.phoneNumber}</div>
                </span>
              </button>
            </div>
          </div>
        </div>
      </td>
    </>
  );
};

const GuestTableRow = ({
  isEditingTable,
  setIsEditingTable,
  item,
  setSelectedTable,
  loadingAddTable,
  handleEditTable,
  isAdditional = false,
  numOfTables,
  resId = false,
}) => {
  return (
    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 w-24">
      <p className="flex items-center font-bold justify-between">
        {isEditingTable !== item.id &&
          (item.table ? "Stol " + item.table : "Nema stol")}
        {isEditingTable === item.id && (
          <select
            className="rounded-2xl h-fit w-fit text-xs"
            onChange={(e) => setSelectedTable(e.target.value)}
            defaultValue={item.table}
          >
            <option key={0} value={0}>
              Bez stola
            </option>
            {Array.from({ length: numOfTables }, (_, i) => i + 1).map(
              (table) => (
                <option key={table} value={table}>
                  Stol {table}
                </option>
              ),
            )}
          </select>
        )}
        <span
          onClick={() => {
            isEditingTable === item.id
              ? setIsEditingTable(null)
              : setIsEditingTable(item.id);
            setSelectedTable(item.table);
          }}
          className={`pl-6 cursor-pointer ${
            isEditingTable === item.id ? "text-red-500" : "text-teal-600"
          }`}
        >
          {isEditingTable === item.id && loadingAddTable ? (
            <span
              className="animate-spin inline-block w-6 h-6 border-4 border-current border-t-transparent text-teal-600 rounded-full"
              role="status"
              aria-label="loading"
            ></span>
          ) : isEditingTable === item.id ? (
            <SVGS.PencilOffSVG />
          ) : (
            <SVGS.PencilSVG />
          )}
        </span>

        {!loadingAddTable && isEditingTable === item.id && (
          <span
            onClick={() => handleEditTable(item.id, isAdditional, resId)}
            className="pl-6 cursor-pointer text-teal-600"
          >
            <SVGS.CheckmarkSVG />
          </span>
        )}
      </p>
    </td>
  );
};
